/**
 * 浮层类返回守卫（React 侧薄封装）
 * -----------------------------------------------------------------------------
 * 手机上没有「关闭浮层」的键 —— iOS 左缘右滑、安卓返回键走的都是 `history back`。
 * 浮层不接这一下，返回手势就会穿到**页面层**的守卫上：
 * 报名表单里点开取景示例 / 省市区选择，划一下就弹出「退出填写？」——
 * 而这正是 2026-10-26 真机反馈的现象。
 *
 * 所以凡是「盖在页面上的东西」都要自己占一层：返回 = 关掉它，页面不动。
 * 判定与历史记账全在 `lib/back-guard.ts`，这里只负责把 React 的可见性
 * 与 `pushBackGuard` 的生命周期对齐。
 *
 * ⚠️ `onDismiss` 走 ref：它常常是每次渲染新建的箭头函数，写进依赖会让
 * effect 反复重跑 —— 每重跑一次就往历史里压一条同 URL 的哨兵，越积越多。
 *
 * 用法：
 * ```tsx
 * useDismissBackGuard(open, close);
 * ```
 */
import { useEffect, useRef } from 'react';
import { pushBackGuard } from './back-guard';

export function useDismissBackGuard(active: boolean, onDismiss: () => void): void {
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!active) return;
    const guard = pushBackGuard(() => {
      dismissRef.current();
      return 'dismiss';
    });
    // `true`：点按钮关掉浮层时哨兵还压着，必须弹掉，否则用户下一次返回会「停在原地」。
    // 手势关掉时哨兵已经被弹走了（`sentinelAlive` 为 false），这里不会多退一格。
    return () => guard.release(true);
  }, [active]);
}
